import { NgTemplateOutlet } from '@angular/common';
import type { SimpleChange, SimpleChanges, TemplateRef } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  output,
  ViewChild,
  ViewContainerRef,
  inject,
  input,
} from '@angular/core';
import type { SafeResourceUrl } from '@angular/platform-browser';

import type { Observable } from 'rxjs';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

import { InfoboxComponent } from '@core/components-infobox-legacy';
import type { APIFileStatusValues } from '@core/http-types';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { IntercomService } from '@core/intercom-integration-legacy';
import { PdfViewerComponent } from '@core/pdf-viewer-smart-ui';
import { getSignedUrlTimeRemainingMs } from '@core/time-legacy';
import type { FileReadResult, IconName, TooltipPosition } from '@core/user-interface-legacy';
import {
  BoxComponent,
  ButtonComponent,
  DialogService,
  DialogWidth,
  DividerComponent,
  EmptyStateComponent,
  FileHandlerComponent,
  FileReadStatus,
  FileTypesCategoryEnum,
  HeadingComponent,
  IconComponent,
  RtStatefulComponent,
  SpinnerComponent,
} from '@core/user-interface-legacy';

import { CvActionsService } from '../services/cv-actions.service';
import type { CvActionsComponent } from './cv-actions/cv-actions.component';
import { CvDeleteDialogComponent } from './cv-delete-dialog/cv-delete-dialog.component';
import { CvParseDialogComponent } from './cv-parse-dialog/cv-parse-dialog.component';
import { ReParseDialogComponent } from './re-parse-dialog/re-parse-dialog.component';

/**
  @name CandidateProfileResume
  @kind Candidate Profile
  @desc Description of CandidateProfileResume

  @example
  <rt-candidate-profile-resume
    [processingStatus]="'ok'"
    [url]="'https://recruitee.s3-eu-central-1.amazonaws.com/candidates/201/pdf_cv_1415320864.pdf'"
    [originalUrl]="'https://recruitee.s3-eu-central-1.amazonaws.com/candidates/201/pdf_cv_1415320864.pdf'"
    [editable]="true">
  </rt-candidate-profile-resume>

  @example
  <rt-candidate-profile-resume
    [processingStatus]="'processing'"
    [url]="null"
    [originalUrl]="'https://recruitee.s3-eu-central-1.amazonaws.com/candidates/201/pdf_cv_1415320864.pdf'"
    [editable]="true">
  </rt-candidate-profile-resume>

 @example
  <rt-candidate-profile-resume
    [processingStatus]="'error'"
    [url]="null"
    [originalUrl]="'https://recruitee.s3-eu-central-1.amazonaws.com/candidates/201/pdf_cv_1415320864.pdf'"
    [editable]="true">
  </rt-candidate-profile-resume>

  @example
  <rt-candidate-profile-resume
    [processingStatus]="null"
    [url]="null"
    [originalUrl]="'https://recruitee.s3-eu-central-1.amazonaws.com/candidates/201/pdf_cv_1415320864.pdf'"
    [editable]="true">
  </rt-candidate-profile-resume>

  @example
  <rt-candidate-profile-resume
    [processingStatus]="'ok'"
    [url]="'https://recruitee.s3-eu-central-1.amazonaws.com/candidates/201/pdf_cv_1415320864.pdf'"
    [originalUrl]="'https://recruitee.s3-eu-central-1.amazonaws.com/candidates/201/pdf_cv_1415320864.pdf'"
    [editable]="true"
    [loading]="true">
  </rt-candidate-profile-resume>

  @example
  <rt-candidate-profile-resume
    [processingStatus]="'ok'"
    [url]="'https://recruitee.s3-eu-central-1.amazonaws.com/candidates/201/pdf_cv_1415320864.pdf'"
    [originalUrl]="'https://recruitee.s3-eu-central-1.amazonaws.com/candidates/201/pdf_cv_1415320864.pdf'"
    [editable]="true"
    [error]="'Upload failed. Try again or contact our support.'">
  </rt-candidate-profile-resume>

  @example
  <rt-candidate-profile-resume
   [processingStatus]="'ok'"
   [url]="'https://recruitee.s3-eu-central-1.amazonaws.com/candidates/201/pdf_cv_1415320864.pdf'"
   [originalUrl]="'https://recruitee.s3-eu-central-1.amazonaws.com/candidates/201/pdf_cv_1415320864.pdf'">
  </rt-candidate-profile-resume>
*/

enum CvState {
  Empty,
  Processing,
  ProcessingFailed,
  Loading,
  Ready,
}

type State = {
  dropdownOpen: boolean;
  cvState: CvState;
};

@Component({
  selector: 'rt-candidate-profile-resume',
  templateUrl: './candidate-profile-resume.component.html',
  styleUrls: ['./candidate-profile-resume.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BoxComponent,
    HeadingComponent,
    NgTemplateOutlet,
    FileHandlerComponent,
    DividerComponent,
    EmptyStateComponent,
    ButtonComponent,
    IconComponent,
    PdfViewerComponent,
    SpinnerComponent,
    TranslatePipe,
    InfoboxComponent,
  ],
})
export class CandidateProfileResumeComponent extends RtStatefulComponent<State> {
  private readonly dialog = inject(DialogService);
  private readonly vcRef = inject(ViewContainerRef);
  private readonly cvActions = inject(CvActionsService);
  private readonly i18n = inject(I18nService);
  private readonly intercom = inject(IntercomService);

  protected readonly cdRef: ChangeDetectorRef;

  public readonly CvState: typeof CvState = CvState;
  public readonly FileTypesCategory = FileTypesCategoryEnum;

  public readonly emptyState = {
    title: this.i18n.translate('app.candidate.empty_cv.title'),
    desc: this.i18n.translate('app.candidate.empty_cv.desc'),
    icon: <IconName>'file-account',
  };

  public readonly candidateName = input<string>(undefined!);
  public readonly candidateId = input<number>(undefined!);
  public readonly url = input<string>(undefined!);
  public readonly processingStatus = input<APIFileStatusValues>(undefined!);
  public readonly tooltipPosition = input<TooltipPosition>('above');
  public readonly editable = input<boolean>(false);
  public readonly loading = input<boolean>(false);
  public readonly outsideTheBox = input<boolean>(false);
  public readonly hasParsedFields = input<boolean>(false);
  public readonly hasNewStyling = input<boolean>(false);
  public readonly cvActionsTpl = input<TemplateRef<CvActionsComponent>>(undefined!);
  @Input() public originalUrl: string;
  @Input() public error: string[];

  public uploadParse = output<{
    resume: File;
    parse: boolean;
  }>();
  public deleteParse = output<{
    deleteParsedData: boolean;
  }>();
  public iframeLoad = output<HTMLIFrameElement>();

  @ViewChild(FileHandlerComponent)
  public fileHandlerComponent: FileHandlerComponent;

  public assetUrl: string | null | SafeResourceUrl;
  public isCvEmpty: boolean;

  private ngOnDestroy$ = new Subject<void>();
  private resume: File;

  constructor() {
    const cdRef = inject(ChangeDetectorRef);

    super(cdRef);
    this.cdRef = cdRef;

    this.createState({
      dropdownOpen: false,
      cvState: CvState.Loading,
    });
  }

  public ngOnInit(): void {
    this.cvActions.inputClick$
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(() => this.fileHandlerComponent.instance.readFileFromDisk());

    this.cvActions.deleteClick$
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(() => this.deleteCv());

    this.cvActions.reparseClick$
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(() => this.reparseCv());
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.url && changes.url.currentValue) {
      if (this.didFilenameChanged(changes.url) || this.didUrlExpired(changes.url.previousValue)) {
        if (this.didUrlExpired(changes.url.currentValue)) {
          this.assetUrl = null;
        } else {
          this.assetUrl = encodeURIComponent(changes.url.currentValue);
        }
      }
    }

    let cvState: CvState = this.state.cvState;

    const processing = this.isProcessing;
    const processingError = this.isProcessingError;

    const loading = this.loading();
    const url = this.url();
    const processingStatus = this.processingStatus();
    if (loading) {
      cvState = CvState.Loading;
    } else if (processing) {
      cvState = CvState.Processing;
    } else if (processingError) {
      cvState = CvState.ProcessingFailed;
    } else if (url || (this.originalUrl && processingStatus === 'anonymization_error')) {
      cvState = CvState.Ready;
    } else {
      cvState = CvState.Empty;
    }

    this.setState(
      {
        ...this.state,
        cvState,
      },
      false,
    );

    this.isCvEmpty = cvState !== CvState.Ready;

    this.cvActions.actionsData$.next({
      editable: this.editable(),
      isCvEmpty: this.isCvEmpty,
      loading: loading,
      originalUrl: this.originalUrl,
      url: url,
      processing,
      anonymizationFailed: processingStatus === 'anonymization_error',
    });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public get isProcessing(): boolean {
    return this.processingStatus() === 'pending';
  }

  public get isProcessingError(): boolean {
    return this.processingStatus() === 'error';
  }

  public changeCv(result: FileReadResult): void {
    const file = <File>result.files;

    if (result.status !== FileReadStatus.OK) {
      return;
    }

    if (this.isCvEmpty && !this.hasParsedFields()) {
      this.doUploadParse(file, true);
    } else {
      this.resume = file;
      this.parseDialog$().subscribe(payload => {
        if (payload && payload.confirmed) {
          this.doUploadParse(this.resume, payload.parse);
        }
      });
    }
  }

  public deleteCv(): void {
    this.deleteDialog$()
      .pipe(
        filter(payload => payload.confirmed),
        take(1),
      )
      .subscribe(payload => {
        this.deleteParse.emit({ deleteParsedData: payload.deleteParsedData });
      });
  }

  public reparseCv(): void {
    this.dialog.openCustom(
      ReParseDialogComponent,
      { width: DialogWidth.SmallX },
      { candidateId: this.candidateId() },
      this.vcRef,
    );
  }

  public didFilenameChanged(cvUrlChange: SimpleChange): boolean {
    return (
      cvUrlChange.previousValue == null ||
      this.getBaseUrl(cvUrlChange.previousValue) !== this.getBaseUrl(cvUrlChange.currentValue)
    );
  }

  public getBaseUrl(url: string): string | null {
    const match = url.match(/.*\.pdf?/);
    return match && match[0] ? match[0] : null;
  }

  public didUrlExpired(url: string): boolean {
    const remaining = getSignedUrlTimeRemainingMs(url);
    return remaining !== null && remaining <= 0;
  }

  public toggleDropdown(dropdownOpen: boolean): void {
    this.setState({ ...this.state, dropdownOpen });
  }

  public openSupport(): void {
    this.intercom.open();
  }

  private doUploadParse(resume: File, parse: boolean): void {
    this.uploadParse.emit({ resume, parse });
  }

  private parseDialog$(): Observable<{ confirmed: boolean; parse: boolean }> {
    return this.dialog
      .openCustom(
        CvParseDialogComponent,
        { width: DialogWidth.SmallX },
        { candidateName: this.candidateName() },
        this.vcRef,
      )
      .afterClosed.pipe(takeUntil(this.ngOnDestroy$.asObservable()));
  }

  private deleteDialog$(): Observable<{ confirmed: boolean; deleteParsedData: boolean }> {
    return this.dialog
      .openCustom(
        CvDeleteDialogComponent,
        { width: DialogWidth.SmallX },
        { candidateName: this.candidateName() },
        this.vcRef,
      )
      .afterClosed.pipe(takeUntil(this.ngOnDestroy$.asObservable()));
  }
}
